import type { DragEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { N30A, N50A } from '@trello/colors';
import { token } from '@trello/theme';

import type { CardLabelType } from './CardLabel.types';

/**
 * The HTML5 Drag and Drop API provides a method to override the default
 * "drag image" while dragging an element, which out-of-the-box is a translucent
 * ghost of the dragged element. Unfortunately, this ghost is hard to style.
 * As a workaround, we clone the label element into a custom drag image element
 * that can be styled. Note that hierarchy is important for styles: e.g.
 * the drag image itself cannot be rotated, but its child can be.
 */
const createDragImage = <T extends HTMLElement>(labelElement: T) => {
  const dragImage = document.createElement('div');
  // The dragImage has to be something that exists elsewhere on the DOM.
  // We position it offscreen as changing its visibility would hide the clone.
  dragImage.style.cssText = `
    position: absolute;
    top: -100vh;
    left: -100vw;
  `;
  document.body.append(dragImage);

  // Clone the label itself as a child of the dragImage.
  const dragClone = labelElement.cloneNode(true) as T;
  dragClone.style.cssText = `
    width: ${labelElement.offsetWidth}px;
    transform: rotate(4deg);
    box-shadow: ${token(
      'elevation.shadow.overlay',
      `0 12px 24px -6px ${N50A}, 0 0 0 1px ${N30A}`,
    )};
    outline: none;
  `;
  dragImage.appendChild(dragClone);

  return dragImage;
};

/**
 * Reusable methods to implement HTML5 drag-and-drop with a label component.
 */
export const useDraggableLabel = <T extends HTMLElement>(
  label: CardLabelType,
  labelRef: RefObject<T>,
) => {
  const dragRef = useRef<HTMLDivElement>();

  const onDragStart = useCallback(
    (e: DragEvent) => {
      /**
       * Store the label ID as a custom data type, prefixed with `trello/`.
       * This data type is handled in app/scripts/views/internal/data-transfer,
       * particularly in Normalize.js and DragDropEvents.js.
       */
      e.dataTransfer.setData('trello/label', label.id);
      e.dataTransfer.effectAllowed = 'copyMove';
      if (labelRef.current) {
        dragRef.current = createDragImage(labelRef.current);
        e.dataTransfer.setDragImage(dragRef.current, 0, 0);
      }
    },
    [label.id, labelRef],
  );

  const onDragEnd = useCallback(() => {
    dragRef.current?.remove();
  }, []);

  // Make sure we remove the dragRef element on unmount.
  useEffect(() => onDragEnd, [onDragEnd]);

  return { onDragStart, onDragEnd };
};
